import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import { useParams } from 'react-router-dom';
import FiltersDropdown from 'feature/dispatching/components/FiltersDropdown';
import FilterChips, { FilterCountChip } from 'feature/dispatching/components/FilterChips';
import { useFilterCount, useUserRolePermissions } from 'hooks';
import { useViewDriverSummaryByLocationQuery } from '../driverStatusEndpoints';

const showV2Features = process.env.REACT_APP_DISPATCHING_VERSION >= '2.0.0';

const boxStyles = {
  backgroundColor: 'surface.light',
  display: 'grid',
  gridTemplateAreas: `
  "t c"
  "s c"
  "f f"`,
  gridTemplateColumns: '1fr 260px',
  p: 3,
  position: 'sticky',
  top: 48,
  zIndex: 2,
};

const LocationHeader = ({
  cardsFilters,
  defaultFiltersState,
  filtersPanel,
  onCreate,
  onRemoveFilter,
}) => {
  const { isDispatchingManage } = useUserRolePermissions();
  const filtersRef = useRef();
  const [filtersOpened, setFiltersOpen] = useState(false);
  const { viewById } = useParams();
  const viewByLocationId = parseInt(viewById);

  const openFilters = R.compose(setFiltersOpen, R.T);
  const closeFilters = R.compose(setFiltersOpen, R.F);

  // prettier-ignore
  const locationFilter = R.compose(
    R.equals(viewByLocationId),
    R.prop('locationId'),
  );

  const {
    location: locationQuery = {},
    isLoading: isLocationsLoading,
    isSuccess: isLocationsSuccess,
  } = useViewDriverSummaryByLocationQuery(undefined, {
    selectFromResult: ({ data, isLoading, isSuccess }) => ({
      isLoading,
      isSuccess,
      location: data?.find(locationFilter),
    }),
  });
  const { address, locationId, name } = locationQuery;

  const title = isLocationsLoading
    ? 'Loading...'
    : isLocationsSuccess && R.isEmpty(locationQuery)
    ? 'No Results'
    : isLocationsSuccess
    ? `${locationId} ${R.toUpper(name)}`
    : ''; // error

  // prettier-ignore
  const filtersIcon = filtersOpened
    ? <ExpandLessRoundedIcon position="right" variant="buttonsChild" />
    : <ExpandMoreRoundedIcon position="right" variant="buttonsChild" />

  const { filterCount } = useFilterCount(cardsFilters, defaultFiltersState);

  return (
    <Box sx={boxStyles}>
      <Typography sx={{ gridArea: 't' }} variant="h5">
        {title}
      </Typography>
      <Typography sx={{ gridArea: 's' }} variant="body2">
        {address}
      </Typography>
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 4, gridArea: 'c' }}>
        <Button
          disabled={!isLocationsSuccess}
          onClick={openFilters}
          onClose={closeFilters}
          ref={filtersRef}
          variant="text"
        >
          Filters
          {!!filterCount && <FilterCountChip count={filterCount} />}
          {filtersIcon}
        </Button>
        {showV2Features && isDispatchingManage && (
          <Button disabled={!isLocationsSuccess} onClick={onCreate}>
            New Driver
          </Button>
        )}
      </Box>
      <Box sx={{ gridArea: 'f' }}>
        <FilterChips
          defaultState={defaultFiltersState}
          filters={cardsFilters}
          onRemove={onRemoveFilter}
        />
      </Box>
      <FiltersDropdown anchorEl={filtersRef.current} onClose={closeFilters} open={filtersOpened}>
        {filtersPanel}
      </FiltersDropdown>
    </Box>
  );
};

LocationHeader.propTypes = {
  cardsFilters: PropTypes.shape({}).isRequired,
  defaultFiltersState: PropTypes.shape({}).isRequired,
  filtersPanel: PropTypes.element.isRequired,
  onCreate: PropTypes.func.isRequired,
  onRemoveFilter: PropTypes.func.isRequired,
};

export default LocationHeader;
